import { Box, Card, Divider, Grid } from '@mui/material';
import { Typography, Tab } from '@uhc-tempo/components';
import React, { useState } from 'react';
import { VISION_COLLECTION_NAMES } from '../../../constants';
import { benefitStyles } from '../../../styles/PageStyles';
import moment from 'moment';
import VisionNoNetworkBenefitsDisclaimer from './VisionNoNetworkBenefitsDisclaimer.component';
/**
 * VisionEstimate component is responsible for displaying the Vision benefits details
 * @param {boolean} isDesktopView - boolean value to check if the view is desktop or not
 * @param {object} inNetworkBenefits - in-network benefits object
 * @param {object} outOfNetworkBenefits - out-of-network benefits object
 * @param {*} props
 * @returns the component
 */
export default function VisionEstimateComponent(props) {
    const { isDesktopView, inNetworkBenefits, outOfNetworkBenefits } = props;

    const [tab, setTab] = useState(0);
    const handleChange = (event, index) => {
        setTab(index);
    };

    // Check if costType value is available and defined based on the costType value
    const validateIfValueExists = (costType) => {
        return costType !== null && costType !== undefined;
    };
    //Displaying cost based on the network benefits
    const renderVisionCost = (networkBenefits) => {
        const copay = networkBenefits?.details?.copay;
        const coinsurance = networkBenefits?.details?.coinsurance;
        const allowance = networkBenefits?.details?.allowance;
        const discount = networkBenefits?.details?.discount;
        if (validateIfValueExists(copay)) {
            return `$${copay}`;
        } else if (validateIfValueExists(coinsurance)) {
            return `${coinsurance}% of allowed amount`;
        } else if (validateIfValueExists(allowance)) {
            return `$${allowance.value}`;
        } else if (validateIfValueExists(discount)) {
            return `${discount.value}% of billed charges`;
        } else {
            return '$ - -';
        }
    };
    //Displaying benefit available or limit reached based on the network benefits
    const renderBenefitAvailable = (limits, collectionName, networkBenefits) => {
        let tagStyle = {};
        let textProperties = {};
        let benefitType = '';
        const limitWithUsage = networkBenefits?.limits.find((limit) => limit.usage > 0);

        //limits array will always have only one object returned if usage is > 0 hence hardcoding to 0 for below parameters
        const benefitAvailableTag =
            collectionName == VISION_COLLECTION_NAMES.contact_lenses && limitWithUsage
                ? ` - ${limits[0]?.remaining} boxes left`
                : '';

        if (limits[0]?.remaining === 0) {
            if (networkBenefits?.tradeOffPlan && limits[0]?.nextAvailableDate) {
                tagStyle = benefitStyles.limitReached;
                benefitType = `Limit reached, next available ${moment(
                    limits[0]?.nextAvailableDate
                ).format('MMM DD, YYYY')}`;
            } else {
                tagStyle = benefitStyles.limitReached;
                benefitType = 'Limits reached';
            }
        } else if (limits[0]?.usage === 0) {
            tagStyle = benefitStyles.limitAvailable;
            benefitType = `Benefit available${benefitAvailableTag}`;
        }

        delete tagStyle['color'];
        textProperties = {
            color: tagStyle.textColorHex,
            marginBottom: '0px',
            fontFamily: '"UHC Sans"',
            fontWeight: 600,
            alignSelf: 'center',
            fontSize: '12.64px',
            lineHeight: '16px',
            paragraph: '12px',
        };

        return (
            <Card
                data-testid="benefitsCard"
                display="inline-block"
                style={{ ...tagStyle, display: 'inline-block' }}
                sx={{
                    paddingY: '4px',
                    paddingX: '8px',
                    boxShadow: 0,
                }}
            >
                {' '}
                <Typography.Paragraph style={textProperties}>{benefitType}</Typography.Paragraph>
            </Card>
        );
    };
    //Displaying benefit limit based on the network benefits
    const benefitLimit = (networkBenefitLimits) => {
        // Check if any limit has usage greater than zero
        const allBenefitsAvailable = networkBenefitLimits?.every((limit) => limit?.usage === 0);
        const allBenefitsPartiallyUsed = networkBenefitLimits?.every(
            (limit) => limit?.remaining > 0
        );
        const usedLimits = networkBenefitLimits?.filter(
            (limit) => limit?.usage > 0 && limit?.remaining != 0
        );
        let displayLimits;
        if (allBenefitsAvailable) {
            displayLimits = networkBenefitLimits;
        } else if (allBenefitsPartiallyUsed) {
            displayLimits = [usedLimits?.[0]];
        } else {
            displayLimits = networkBenefitLimits.filter((limit) => limit?.remaining === 0);
        }
        return displayLimits;
    };
    //Check if cost is available based on the network benefits
    const isCostAvailable = (details) => {
        if (
            validateIfValueExists(details?.copay) ||
            validateIfValueExists(details?.coinsurance) ||
            validateIfValueExists(details?.allowance) ||
            validateIfValueExists(details?.discount)
        ) {
            return true;
        } else {
            return false;
        }
    };

    //Displaying dislcimer for no cost information
    const renderDisclaimer = (index) => {
        const networkBenefits = index == 0 ? inNetworkBenefits : outOfNetworkBenefits;
        //display disclaimer only if noBenefits
        if (!networkBenefits) {
            return <VisionNoNetworkBenefitsDisclaimer />;
        }
        if (isCostAvailable(networkBenefits?.details)) {
            return null;
        } else {
            return (
                <Grid item xs={12}>
                    <VisionNoNetworkBenefitsDisclaimer />
                </Grid>
            );
        }
    };
    //Displaying allowance text based on the collection name
    const renderAllowanceText = (networkBenefits, collectionName) => {
        const allowance = networkBenefits?.details?.allowance;
        if (collectionName == VISION_COLLECTION_NAMES.frames && validateIfValueExists(allowance))
            return 'Eyeglass frame allowance';
        else if (
            collectionName == VISION_COLLECTION_NAMES.lenses &&
            validateIfValueExists(allowance)
        )
            return 'Eyeglass lens allowance';
        else if (
            collectionName == VISION_COLLECTION_NAMES.contact_lenses &&
            validateIfValueExists(allowance)
        )
            return 'Contact lens allowance';
        else return 'You pay';
    };
    //Displaying allowance sub text based on the collection name
    const renderAllowanceSubText = (networkBenefits, collectionName) => {
        const allowance = networkBenefits?.details?.allowance;
        const overAllowance = networkBenefits?.details?.overAllowance;
        switch (collectionName) {
            case VISION_COLLECTION_NAMES.frames:
                if (validateIfValueExists(allowance) && validateIfValueExists(overAllowance)) {
                    return `If the frame costs more than your $${parseInt(
                        allowance.value
                    )} allowance, then you're responsible for ${parseInt(
                        overAllowance.value
                    )}% of the remaining balance.`;
                }
                break;
            case VISION_COLLECTION_NAMES.lenses:
                if (validateIfValueExists(allowance) && validateIfValueExists(overAllowance)) {
                    return `If the lenses cost more than your $${parseInt(
                        allowance.value
                    )} allowance, then you're responsible for ${parseInt(
                        overAllowance.value
                    )}% of the remaining balance.`;
                }
                break;
            case VISION_COLLECTION_NAMES.contact_lenses:
                if (validateIfValueExists(allowance) && validateIfValueExists(overAllowance)) {
                    return `If the contact lenses cost more than your $${parseInt(
                        allowance.value
                    )} allowance, then you're responsible for ${parseInt(
                        overAllowance.value
                    )}% of the remaining balance.`;
                }
                break;
            default:
                return '';
        }
    };
    //Format limit description based on the limit sub description text
    const formatLimitDesc = (limit) => {
        if (limit.limitSubDescription.includes('Daily/Biweekly Wear')) {
            return `Daily/Biweekly Wear (up to ${limit.occurrencesPerPeriod} Boxes)`;
        } else if (limit.limitSubDescription.includes('Monthly Wear')) {
            return `Monthly Wear (up to ${limit.occurrencesPerPeriod} Boxes)`;
        } else if (limit.limitSubDescription.includes('Necessary Contact')) {
            return `Extended Wear (${limit.occurrencesPerPeriod} Boxes)`;
        } else {
            return limit.limitSubDescription;
        }
    };
    //Displaying content based on the tab selected
    const renderTabContent = (index) => {
        const networkBenefits = index == 0 ? inNetworkBenefits : outOfNetworkBenefits;
        if (!networkBenefits) return null;
        const collectionName = networkBenefits?.collectionName;
        const limits = benefitLimit(networkBenefits?.limits);
        const dailyWearTexts = [];
        const monthlyWearTexts = [];

        for (const limit of limits) {
            let yearType = '';
            switch (limit.timeframeUnit) {
                case 'P':
                    yearType = 'plan';
                    break;
                case 'Y':
                    yearType = 'calendar';
                    break;
                default:
                    yearType = '';
            }
            const dailyWearText = formatLimitDesc(limit);
            dailyWearTexts.push(dailyWearText);

            const monthlyWearText = `Limited to ${limit.occurrencesPerPeriod} every ${
                limit.timeframe
            } ${yearType} ${limit.timeframeUnitDescription.toLowerCase()}(s)`;
            monthlyWearTexts.push(monthlyWearText);
        }
        return (
            <Grid container>
                <Grid item xs={12}>
                    <Typography.Paragraph
                        data-testid="visionBenefitCoverageText"
                        style={{
                            fontFamily: '"UHC Sans"',
                            fontWeight: 700,
                            fontSize: '25.63px',
                            lineHeight: '32px',
                            color: '#002677',
                            paragraph: '16px',
                        }}
                    >
                        Vision benefit coverage
                    </Typography.Paragraph>
                </Grid>
                <Grid item xs={12} gap="4px">
                    <Typography.Paragraph
                        data-testid="wearText"
                        style={{
                            fontFamily: '"UHC Sans"',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '19.2px',
                            color: '#333333',
                            paragraph: '12px',
                        }}
                    >
                        {collectionName === VISION_COLLECTION_NAMES.contact_lenses
                            ? dailyWearTexts.map((text, index) => (
                                  <React.Fragment key={index}>
                                      {text}
                                      <br />
                                  </React.Fragment>
                              ))
                            : monthlyWearTexts.map((text, index) => (
                                  <React.Fragment key={index}>
                                      {text}
                                      <br />
                                  </React.Fragment>
                              ))}
                    </Typography.Paragraph>
                    <Typography.Paragraph
                        data-testid="inNetworkText"
                        style={{
                            fontFamily: '"UHC Sans"',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '19.2px',
                            color: '#333333',
                            paragraph: '12px',
                        }}
                    >
                        {index == 0
                            ? 'When you visit a network provider'
                            : 'When you visit an out-of-network provider'}
                    </Typography.Paragraph>
                </Grid>
                <Grid item xs={isDesktopView ? 6 : 12} marginTop="16px">
                    <Grid container>
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    borderTop: 1,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    paddingTop: '16px',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography.H6
                                        data-testid="serviceTypeText"
                                        style={{
                                            color: '#333333',
                                            fontWeight: 700,
                                            fontSize: '18px',
                                        }}
                                    >
                                        {renderAllowanceText(networkBenefits, collectionName)}
                                    </Typography.H6>
                                    <Typography.H6
                                        data-testid="visionCost"
                                        style={{
                                            color: '#333333',
                                            fontWeight: 700,
                                            fontSize: '18px',
                                            alignSelf: 'right',
                                        }}
                                    >
                                        {renderVisionCost(networkBenefits)}
                                        {(collectionName ==
                                            VISION_COLLECTION_NAMES.contact_lenses ||
                                            collectionName == VISION_COLLECTION_NAMES.lenses) &&
                                        networkBenefits?.details?.copay != null ? (
                                            <span
                                                style={{
                                                    fontWeight: 500,
                                                    color: '#333333',
                                                    fontSize: '14px',
                                                }}
                                            >{` copay`}</span>
                                        ) : (
                                            ''
                                        )}
                                        {collectionName == VISION_COLLECTION_NAMES.frames &&
                                        limits[0]?.remaining == 0 ? (
                                            <span
                                                style={{
                                                    fontWeight: 500,
                                                    color: '#C40000',
                                                    fontSize: '14px',
                                                }}
                                            >{` (used)`}</span>
                                        ) : (
                                            ''
                                        )}
                                    </Typography.H6>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '16px',
                                    }}
                                >
                                    {renderBenefitAvailable(
                                        limits,
                                        collectionName,
                                        networkBenefits
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} paddingTop="16px">
                            <Typography.Paragraph
                                data-testid="overagePercentText"
                                style={{
                                    fontFamily: '"UHC Sans"',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '19.2px',
                                    color: '#333333',
                                    paragraph: '12px',
                                }}
                            >
                                {renderAllowanceSubText(networkBenefits, collectionName)}
                            </Typography.Paragraph>
                        </Grid>
                        <Grid item xs={12} paddingTop="16px">
                            {networkBenefits?.tradeOffPlan && (
                                <Typography.Paragraph
                                    data-testid="visionTradeOffText"
                                    style={{
                                        fontFamily: '"UHC Sans"',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '19.2px',
                                        color: '#333333',
                                        paragraph: '12px',
                                    }}
                                >
                                    Your vision benefit covers contact lenses or eyeglasses. You can
                                    choose one or the other.
                                </Typography.Paragraph>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} marginTop="16px" marginBottom="16px">
                    <Divider />
                </Grid>
            </Grid>
        );
    };
    return (
        <Grid container>
            <Grid item xs={12} activeTab={0} onTabChange={handleChange}>
                {renderDisclaimer(tab)}
            </Grid>
            <Grid item xs={12} gap="24px" data-testid="visionPage">
                <Tab
                    tabHeadings={['In-network', 'Out-of-network']}
                    activeTab={0}
                    onTabChange={handleChange}
                >
                    <Tab.TabPanel>
                        <div data-testid="visionTabContent">{renderTabContent(tab)}</div>
                    </Tab.TabPanel>
                    <Tab.TabPanel>{renderTabContent(tab)}</Tab.TabPanel>
                </Tab>
            </Grid>
        </Grid>
    );
}
